import { useCallback } from 'react';
import ExclamationFilledIcon from '@shared/assets/icons/v2/exclamation-filled.svg';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { Pill } from '@shared/components/pill';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { Card } from './Card';
import { ActiveCard, DraggableContext, DraggableContextValue } from './DraggableContext';
import DragProxyCardStyles from './DragProxyCardStyles.module.css';
import DragProxyStyles from './DragProxyStyles.module.css';

interface Props {
  activeCards: ActiveCard[];
  workflowTemplate: API.WorkflowTemplateFragment;
  viewTemplate: API.ViewTemplateFragment;
  isDragUpdateEnabled?: boolean;
  boardGroupComponentLabel?: string;
}

const dragProxyState: DraggableContextValue = {
  isDragging: true,
  isDragEnabled: true,
  isDragSortEnabled: true,
  activeCards: [],
};

export function DragProxy({
  activeCards,
  viewTemplate,
  workflowTemplate,
  isDragUpdateEnabled = true,
  boardGroupComponentLabel,
}: Props) {
  const [activeCard, ...otherActiveCards] = activeCards;
  const moreCount = otherActiveCards.length;
  const hasMore = moreCount > 0;
  const { entity } = activeCard;
  const getIsCardBeforePaidCutoff = useCallback(() => false, []);
  const cardProps = {
    entity,
    viewTemplate,
    workflowTemplate,
    cardStyles: DragProxyCardStyles,
    getIsCardBeforePaidCutoff,
  };
  const countLabel = i18n('card.kanban.dragging.items', { count: activeCards.length });
  const dragRestrictionLabel =
    !isDragUpdateEnabled && boardGroupComponentLabel
      ? i18n('card.kanban.dragging.serverManagedGroupRestricted', {
          componentLabel: boardGroupComponentLabel,
        })
      : undefined;

  return (
    <DraggableContext.Provider value={dragProxyState}>
      <View className={DragProxyStyles.proxy}>
        <View className={DragProxyStyles.cardStack}>
          <View className={DragProxyStyles.card1}>
            <Card {...cardProps} />
          </View>
          {hasMore && (
            <View className={DragProxyStyles.card2}>
              <Card {...cardProps} />
            </View>
          )}
        </View>
        {hasMore && (
          <View className={DragProxyStyles.cornerCount}>
            <Pill round borderWidth={1}>
              {countLabel}
            </Pill>
          </View>
        )}
        {dragRestrictionLabel && (
          <View className={DragProxyStyles.dragRestriction}>
            <View className={DragProxyStyles.dragRestrictionPill}>
              <ExclamationFilledIcon />
              <Text size="body2" flex balance>
                {dragRestrictionLabel}
              </Text>
            </View>
          </View>
        )}
      </View>
    </DraggableContext.Provider>
  );
}
